import { useFormContext } from "react-hook-form";
import { useEffect, useRef } from "react";
import { IEventWizardServicesViewModel } from "../../models/eventWizardServicesViewModel";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { IUserModel } from "../../../../../common/infrastructure/hooks/useAuthentication";
import { PageMode } from "../../../../../common/types";

export const useServiceSelection = (
  services: IEventWizardServicesViewModel[] | undefined,
  selectedServiceId: number | undefined,
  studioEmployees?: ISelectFieldModel[],
  currentUser?: IUserModel | null,
  pageMode?: PageMode,
  initialData?: any
) => {
  const { setValue, getValues } = useFormContext();
  const previousServiceId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (selectedServiceId && services) {
      const selectedService = services.find(
        (service) => service.id === selectedServiceId
      );

      if (selectedService) {
        // Detect if this is a genuine service change by the user or just initial form setup
        const isActualServiceChange = previousServiceId.current !== undefined && 
                                     previousServiceId.current !== selectedServiceId;
        
        // In edit mode, only update duration if user is actively changing services
        // In add/duplicate mode, always update duration
        const shouldUpdateDuration = pageMode !== "edit" || isActualServiceChange;

        if (shouldUpdateDuration) {
          setValue("durationInMinutes", selectedService.durationInMinutes);
        }
        setValue("price", selectedService.price);
        
        // Track the current service ID for next comparison
        previousServiceId.current = selectedServiceId;

        // Filter available employees for this service
        const availableEmployees = studioEmployees?.filter((employee) =>
          selectedService.employees.includes(employee.id)
        );

        if (availableEmployees && availableEmployees.length > 0) {
          const currentEmployeeId = getValues("employeeId");

          // Check if currently selected employee is still valid for this service
          const currentEmployeeStillValid = availableEmployees.find(
            (emp) => emp.id === currentEmployeeId
          );

          // If current employee is not valid or empty, select new employee
          if (!currentEmployeeStillValid || !currentEmployeeId) {
            // Priority 1: Select current user if they're in the available employees
            const currentUserAsEmployee = availableEmployees.find(
              (emp) => emp.id === currentUser?.id
            );

            if (currentUserAsEmployee) {
              setValue("employeeId", currentUserAsEmployee.id);
            } else {
              // Priority 2: Select first available employee
              setValue("employeeId", availableEmployees[0].id);
            }
          }
          // If current employee is still valid, keep the selection unchanged
        } else {
          // Clear employee selection if no employees available for this service
          setValue("employeeId", 0);
        }
      }
    }
  }, [selectedServiceId, services, studioEmployees, setValue, getValues, pageMode]);

  // Get filtered employees for the selected service
  const getFilteredEmployees = (serviceId: number): ISelectFieldModel[] => {
    if (!services || !studioEmployees || !serviceId) {
      return studioEmployees || [];
    }

    const selectedService = services.find(
      (service) => service.id === serviceId
    );
    if (!selectedService) {
      return studioEmployees || [];
    }

    return studioEmployees.filter((employee) =>
      selectedService.employees.includes(employee.id)
    );
  };

  return { getFilteredEmployees };
};
